
import { Component, Prop, Vue } from 'vue-property-decorator';
import VFormBuilder from '@/components/shared/form/VFormBuilder.vue';
import VTable from '@/components/shared/table/VTable.vue';
import { InputType } from '@/enums/InputType';
import { ComoGiftStatus } from '@/enums/ComoGiftStatus';
import { ComoManagedGiftType } from '@/enums/ComoManagedGiftType';
import { ComoGift, ComoManagedGift } from '@/interfaces/models/ComoManagedGift';
import { ColumnHeader } from '@/interfaces/components/ColumnHeader';
import { ComoManagedGiftSavePayload } from '@/stores/comoManagedGifts';
import headers from './headers';
import hydratedHeaders from './hydratedHeaders';

@Component({
  components: { VFormBuilder, VTable },
})
export default class ComoManagedGifts extends Vue {
  @Prop({ type: Array, required: true }) public hydratedGifts!: ComoGift[];
  @Prop({ type: Array, required: true }) public managedGifts!: ComoManagedGift[];
  @Prop({ type: Number, required: true }) public total!: number;
  @Prop({ type: Number, required: true }) public limit!: number;
  @Prop({ type: Boolean, default: false }) public loading!: boolean;
  @Prop({ type: Boolean, default: false }) public hydratedLoading!: boolean;
  @Prop({ type: Boolean, default: false }) public saving!: boolean;

  public $refs!: {
    createForm: InstanceType<typeof VFormBuilder> & { form: any; validate: () => Promise<boolean> };
  };

  public createDialog: boolean = false;
  public createValues: any = {};
  public editingGiftId: string = '';
  public filter: { giftType?: string; status?: string; templateKey?: string } = {};
  public headers: ColumnHeader[] = headers;

  get unmappedGifts() {
    return this.hydratedGifts.filter((gift) => !gift.additionalData);
  }

  get dialogTitleKey() {
    return this.editingGiftId ? 'venue.loyaltyManagedGifts.edit' : 'venue.loyaltyManagedGifts.create';
  }

  get hydratedTableItems() {
    return this.hydratedGifts.map((gift) => ({
      ...gift,
      giftType: gift.additionalData?.giftType,
      status: gift.additionalData?.status,
      requiredPoints: gift.additionalData?.requiredPoints,
    }));
  }

  get hydratedHeaders() {
    return hydratedHeaders.map((header) => ({ ...header, text: header.text ? this.$t(header.text) : '' }));
  }

  get giftTypeItems() {
    return Object.values(ComoManagedGiftType).map((value) => ({
      text: this.$t(`venue.loyaltyManagedGifts.giftTypes.${value}`),
      value,
    }));
  }

  get statusItems() {
    return Object.values(ComoGiftStatus).map((value) => ({
      text: this.$t(`venue.loyaltyManagedGifts.statuses.${value}`),
      value,
    }));
  }

  get createItems() {
    return [
      {
        name: 'templateKey',
        type: InputType.Text,
        label: 'venue.loyaltyManagedGifts.templateKey',
        rules: (form: any) => (form.giftId ? '' : 'required'),
      },
      {
        name: 'giftId',
        type: InputType.Text,
        label: 'venue.loyaltyManagedGifts.giftId',
        rules: (form: any) => (form.templateKey ? '' : 'required'),
      },
      { name: 'name', type: InputType.Language, label: 'venue.loyaltyManagedGifts.name', rules: 'required' },
      { name: 'description', type: InputType.Language, label: 'venue.loyaltyManagedGifts.description' },
      {
        name: 'giftType',
        type: InputType.Select,
        label: 'venue.loyaltyManagedGifts.type',
        items: this.giftTypeItems,
        rules: 'required',
      },
      {
        name: 'requiredPoints',
        type: InputType.Text,
        label: 'venue.loyaltyManagedGifts.requiredPoints',
        rules: 'required|integer|min_value:0',
        default: 0,
      },
      {
        name: 'status',
        type: InputType.Select,
        label: 'venue.loyaltyManagedGifts.status',
        items: this.statusItems,
        rules: 'required',
        default: ComoGiftStatus.ACTIVE,
      },
      { name: 'image', type: InputType.File, label: 'venue.loyaltyManagedGifts.image', accept: 'image/*' },
    ];
  }

  public requestManagedGifts(page = { page: 1, limit: this.limit }) {
    this.$emit('page', { ...page, filter: this.filter });
  }

  public openCreate(gift?: ComoGift) {
    this.editingGiftId = '';
    this.createValues = {
      templateKey: gift?.templateKey || '',
      giftId: gift?.key || '',
      name: { en: '' },
      description: {},
      giftType: ComoManagedGiftType.REWARD,
      status: ComoGiftStatus.ACTIVE,
    };
    this.createDialog = true;
  }

  public openEdit(gift: ComoManagedGift) {
    this.editingGiftId = gift._id || '';
    this.createValues = { ...gift };
    this.createDialog = true;
  }

  public async saveGift() {
    if (!(await this.$refs.createForm.validate())) {
      return;
    }

    const form = this.$refs.createForm.form;
    const { image, ...data } = form;
    this.createDialog = false;
    this.$emit('save', {
      id: this.editingGiftId || undefined,
      data,
      image: image instanceof File ? image : undefined,
    } as ComoManagedGiftSavePayload);
  }

  public async deleteGift(gift: ComoManagedGift) {
    if (!gift._id || !(await this.$confirm(this.$t('venue.loyaltyManagedGifts.deleteQuestion') as string))) {
      return;
    }

    this.$emit('delete', gift);
  }
}
